import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../hooks/useStore';
import { Trophy, Calendar } from 'lucide-react';

/**
 * Public events index at `/e`. Shows currently-live tournaments + recent past
 * events. Friendly off-season state when nothing is active. This is the page
 * a fan visits if they don't have a direct event link.
 */
export function EventIndexPage() {
  const db = useStore((d) => d);
  const tournaments = useMemo(
    () => [...db.tournaments].filter((t) => t.status !== 'archived').sort((a, b) => b.startsAt.localeCompare(a.startsAt)),
    [db.tournaments],
  );

  const live = tournaments.filter((t) => t.status === 'in_progress' || t.status === 'paused' || t.status === 'scheduled');
  const past = tournaments.filter((t) => t.status === 'completed').slice(0, 6);

  return (
    <>
      <section className="card card--accent" style={{ padding: 22 }}>
        <div className="eyebrow">Events</div>
        <h2 style={{ margin: '4px 0 4px', fontSize: 26 }}>
          {live.length > 0 ? 'Pick your event' : 'See you next event'}
        </h2>
        <p className="muted" style={{ margin: 0 }}>
          {live.length > 0
            ? 'Live and upcoming tournaments. Pick one to see scores, schedule and your team room.'
            : 'No tournaments are running right now. Check back when the next one starts.'}
        </p>
      </section>

      {live.length > 0 && (
        <div className="grid grid--2" style={{ marginTop: 16 }}>
          {live.map((t) => (
            <Link key={t.id} to={`/events/${t.slug}`} className="card" style={{ textDecoration: 'none' }}>
              <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                <Trophy size={22} style={{ color: 'var(--brand-arc)' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{t.name}</div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    <Calendar size={12} style={{ verticalAlign: -2 }} />{' '}
                    {new Date(t.startsAt).toLocaleDateString()} – {new Date(t.endsAt).toLocaleDateString()}
                  </div>
                </div>
                <span className={`chip chip--${t.status === 'in_progress' ? 'ok' : 'info'}`}>
                  <span className="chip__dot" />
                  {t.status.replace('_', ' ')}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {past.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card__head">
            <div>
              <h3 className="card__title">Past events</h3>
              <p className="card__sub">Recent recaps and standings.</p>
            </div>
          </div>
          <table className="table">
            <thead><tr><th>Name</th><th>Dates</th></tr></thead>
            <tbody>
              {past.map((t) => (
                <tr key={t.id}>
                  <td><Link to={`/events/${t.slug}`} style={{ fontWeight: 600 }}>{t.name}</Link></td>
                  <td className="muted">{new Date(t.startsAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
